/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, IActionContext, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { format, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    IHerbalBusinessSignUpConfig,
    IHerbalBusinessSignUpProps,
    IHerbalBusinessSignUpResources
} from './herbal-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpSelect, SignUpFileUpload, SignUpLabel } from './components';
import { OptionHTMLAttributes } from 'react';
import UserDataService from './axios/services/user.service';
import IUserData from './axios/type/comment-data';

export interface IBusinessSignUpExtendedProps extends IHerbalBusinessSignUpProps<IHerbalBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    isAccountExist: boolean;
    isStudent: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    accountExistMessage: React.ReactNode[];
    formDescription: React.ReactNode;
    disclaimer: React.ReactNode;
    indicatedText: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    hasError?: boolean;
    errorMessage?: string;
    placeHolder?: string;
    multiple?: boolean;
    label: string;
    value: string;
    optionList?: OptionHTMLAttributes<any>[];
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    helpText?: string;
    fieldInputReference?: React.RefObject<HTMLInputElement>;
    fieldSelectReference?: React.RefObject<HTMLSelectElement>;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    File = 'file',
    Select = 'select',
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea'
}
export enum InputTypeOptions {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Student = 'Student Naturopath | Herbalist'
}

export enum InputID {
    Type = 'Type',
    BusinessName = 'BusinessName',
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    AssociationInstitution = 'Association/Institution',
    MembershipStudentNo = 'Membership/Student No',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    FieldAttachment = 'FieldAttachment'
}

export const renderInputSection = (
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    onInputBlur: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
    showErrors: boolean,
    visibleFileAttachField: boolean | undefined,
    resources: IHerbalBusinessSignUpResources,
    config: IHerbalBusinessSignUpConfig,
    isStudent: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void,
    onSelectChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void,
    onFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
): IBusinessSignUpItem[] => {
    let inputsUpdate: IFormItem[] = inputs;
    const inputsUpdateCheckExist: IFormItem[] = inputsUpdate.filter((element, index) => element.id === InputID.FieldAttachment);

    if (inputsUpdateCheckExist.length) {
        inputsUpdate = inputsUpdate.map(item => {
            let input = item;
            if (input.id === InputID.FieldAttachment) {
                input = {
                    ...input,
                    helpText: isStudent ? config.FieldAttachmentStudentHelpText : config.FieldAttachmentPractitionerHelpText
                };
            }
            return input;
        });
    }

    return inputsUpdate.map((element, index) => {
        const {
            id,
            type,
            placeHolder,
            hasError,
            errorMessage,
            label,
            maxChars,
            isRequired,
            pattern,
            customClass,
            value,
            optionList,
            fieldInputReference,
            fieldSelectReference,
            multiple,
            helpText
        } = element;
        const isRequiredLabel = isRequired ? '*' : '';
        const fieldLabel = `${label}  ${isRequiredLabel}`;

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: fieldLabel }} />,
            errorMessage: hasError && <SignUpError {...{ className, type: 'item', message: errorMessage }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else {
            if (type === 'select') {
                baseSignUpItem.input = (
                    <SignUpSelect
                        {...{
                            id,
                            value,
                            optionList,
                            className,
                            isRequired: isRequired === undefined ? false : isRequired,
                            onChange: onSelectChange,
                            fieldSelectReference
                        }}
                    />
                );
            } else if (type === 'file') {
                baseSignUpItem.input = (
                    <SignUpFileUpload
                        {...{
                            id,
                            type,
                            className,
                            isRequired: isRequired === undefined ? false : isRequired,
                            onChange: onFileChange,
                            // onBlur: onInputBlur,
                            fieldInputReference,
                            multiple: multiple ? true : false,
                            helpText: helpText
                        }}
                    />
                );
            } else {
                baseSignUpItem.input = (
                    <SignUpInput
                        {...{
                            id,
                            type,
                            placeHolder,
                            value,
                            maxLength: maxChars,
                            pattern,
                            className,
                            isRequired: isRequired === undefined ? false : isRequired,
                            onChange: onInputChange,
                            onBlur: onInputBlur,
                            fieldInputReference
                        }}
                    />
                );
            }
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IHerbalBusinessSignUpProps<IHerbalBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    // Used as default data
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.Type,
            type: InputType.Select,
            label: this.props.resources.type,
            value: 'Herbalist | Naturopath',
            isRequired: true,
            customClass: 'width-100',
            optionList: [
                {
                    disabled: false,
                    label: 'Herbalist | Naturopath',
                    selected: true,
                    value: 'Herbalist | Naturopath'
                },
                {
                    disabled: false,
                    label: 'Homoeopath',
                    selected: false,
                    value: 'Homoeopath'
                },
                {
                    disabled: false,
                    label: 'TCM | Acupuncturist',
                    selected: false,
                    value: 'TCM | Acupuncturist'
                },
                {
                    disabled: false,
                    label: 'Medical Doctor | Specialist',
                    selected: false,
                    value: 'Medical Doctor | Specialist'
                },
                {
                    disabled: false,
                    label: 'Dentist',
                    selected: false,
                    value: 'Dentist'
                },
                {
                    disabled: false,
                    label: 'Chiropractor | Osteopath',
                    selected: false,
                    value: 'Chiropractor | Osteopath'
                },
                {
                    disabled: false,
                    label: InputTypeOptions.Student,
                    selected: false,
                    value: InputTypeOptions.Student
                },
                {
                    disabled: false,
                    label: 'Wholesaler',
                    selected: false,
                    value: 'Wholesaler'
                },
                {
                    disabled: false,
                    label: 'Contract Customer | Manufacturer',
                    selected: false,
                    value: 'Contract Customer | Manufacturer'
                }
            ]
        },
        {
            id: InputID.BusinessName,
            type: InputType.Text,
            placeHolder: '',
            label: this.props.resources.BusinessNameLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.FirstName,
            type: InputType.Text,
            placeHolder: '',
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            placeHolder: '',
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            placeHolder: 'herbalextractcompany@gmail.com',
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-100'
        },
        {
            id: InputID.AssociationInstitution,
            type: InputType.Text,
            placeHolder: '',
            label: this.props.resources.associationInstitution,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.MembershipStudentNo,
            type: InputType.Text,
            placeHolder: '',
            label: this.props.resources.membershipStudentNo,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.FieldAttachment,
            type: InputType.File,
            placeHolder: '',
            label: this.props.resources.FieldAttachment,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            multiple: true,
            helpText: this.props.config.FieldAttachmentPractitionerHelpText
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            placeHolder: '',
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        }
    ];

    private readonly formChildInputReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly moduleClassName: string = 'ms-business-sign-up';

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(props.config),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            isAccountExist: false,
            isStudent: false
        };

        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildInputReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady, formItems, isAccountExist } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage,
            accountExistMessage
        } = resources;
        const { IndicatedTextForStudentHelpText } = config;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;
        const companyEmail = this._getItem(InputID.Email);

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange,
                this._onInputBlur,
                showAddressErrors,
                config.visibleFileAttachField,
                resources,
                config,
                this.state.isStudent,
                addressForm,
                this._onAddressChange,
                this._onSelectChange,
                this._onFileChange
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            accountExistMessage: [
                isAccountExist && (
                    <SignUpError
                        key='error'
                        id='error'
                        type={'account-exist'}
                        className={this.moduleClassName}
                        message={format(accountExistMessage, companyEmail?.value)}
                    />
                )
            ],
            formDescription: config.formDescription && (
                <RichTextComponent className={`${this.moduleClassName}__formDescription`} text={config.formDescription} />
            ),
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            ),
            indicatedText: <RichTextComponent className={`${this.moduleClassName}__indicatedText`} text={IndicatedTextForStudentHelpText} />
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    private readonly _onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.type === 'file') {
                const fileJson = this._getBase64(event.target.files);
                item.value = fileJson;
                item.value === '' ? (item.hasError = true) : (item.hasError = false);
            } else if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    private _getBase64(files: FileList | null): any {
        if (files === null) {
            return null;
        }
        const allFiles: any[] = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (typeof reader.result !== 'string') {
                    return;
                }
                const fileInfo = {
                    name: file.name,
                    base64: reader.result.split(',').pop()
                };
                allFiles.push(fileInfo);
            };
        }
        return allFiles;
    }

    private readonly _onInputBlur = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.id === event.target.id) {
                // Check required.
                if (!event.target.required) {
                    // Do nothing here.
                } else if (!event.target.value) {
                    item.hasError = true;
                    item.errorMessage = 'This field is required.';
                } else {
                    item.hasError = false;
                    if (event.target.type === 'email') {
                        if (item.pattern !== undefined && item.value.match(item.pattern) === null) {
                            item.hasError = true;
                            item.errorMessage = 'Your email is invalid format.';
                        } else {
                            item.hasError = false;
                        }
                    }
                }
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    private readonly _onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const newForm = this.state.formItems;

        newForm.forEach(item => {
            if (item.type === 'select') {
                item.optionList &&
                    item.optionList.forEach(sub_item => {
                        sub_item.selected = false;
                        if (sub_item.label === event.target.value) {
                            sub_item.selected = true;
                        }
                        if (event.target.value === 'Select type') {
                            item.hasError = true;
                            item.errorMessage = 'This field is required.';
                        } else {
                            item.hasError = false;
                            item.value = event.target.value;
                        }
                    });
            } else if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });
        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        if (event.target.id === InputID.Type) {
            this.setState({
                isStudent: event.target.value === InputTypeOptions.Student ? true : false
            });
        }
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    private _addedReference(config: IHerbalBusinessSignUpConfig): IFormItem[] {
        let newFormFields = this.BusinessSignUpDefaultInputs;
        if (!config.visibleFileAttachField) {
            // Delete attachment!
            newFormFields = newFormFields.filter(item => item.id !== InputID.FieldAttachment);
        }
        newFormFields[0].fieldInputReference = this.formChildInputReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(),
                formItems: newForm,
                addressHasErrors: errors,
                showAddressErrors: false
            });
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern
            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    if (currentInput.type !== 'select') {
                        return false;
                    }
                }
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const newForm = this.state.formItems;
        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event.preventDefault();
            return;
        }

        const { context } = this.props;

        // Check for matching id and if not empty
        const type = this._getItem(InputID.Type);
        const associationInstitution = this._getItem(InputID.AssociationInstitution);
        const membershipStudentNo = this._getItem(InputID.MembershipStudentNo);
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const businessName = this._getItem(InputID.BusinessName);
        const companyName = businessName ? businessName?.value : `Org ${firstName?.value} ${lastName?.value}`;
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);
        const jobTitle = this._getItem(InputID.JobTitle);
        const department = this._getItem(InputID.Department);
        const companySize = this._getItem(InputID.Size);
        const files = this._getItem(InputID.FieldAttachment);
        const jsonFileData = JSON.stringify(files?.value);
        const comments = `FileAttachment: ${jsonFileData}\nType: ${type?.value &&
            this._stringConvertTitleCase(type?.value)}\nAssociation/Institution: ${associationInstitution?.value &&
            this._stringConvertTitleCase(associationInstitution?.value)}\nMembership/StudentNo: ${membershipStudentNo?.value &&
            this._stringConvertTitleCase(membershipStudentNo?.value)}\nModality/Occupation: `;

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }

        const newPartner: BusinessPartnerProspect = {
            FirstName: firstName && this._stringConvertTitleCase(firstName.value),
            LastName: lastName && this._stringConvertTitleCase(lastName.value),
            CompanyName: this._stringConvertTitleCase(companyName),
            Address: companyAddress && companyAddress.address && companyAddress.address,
            JobTitle: jobTitle && jobTitle.value,
            Department: department && department.value,
            CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
            Comments: comments
        };

        const ctx = context.actionContext;
        const userAPIEndpoint = ctx.requestContext.app.config.userAPIEndpoint && ctx.requestContext.app.config.userAPIEndpoint;
        const prospectAPIEndpoint = ctx.requestContext.app.config.prospectAPIEndpoint && ctx.requestContext.app.config.prospectAPIEndpoint;
        // const userAPIEndpoint =
        //     'https://prod-04.australiasoutheast.logic.azure.com:443/workflows/714a5264995b45d08956785121d44d87/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=q1OKGpjUtaY5TIeYmPPp8oqFK-U7mFpnKu9vi0r0fLc';
        // const userAPIEndpoint =
        //     'https://prod-20.australiasoutheast.logic.azure.com:443/workflows/463318d730be40199539b2f461b2e722/triggers/manual/paths/invoke?api-version=2016-06-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=-KrkgPO4MgLm0789n7Y9vMPdFws8SI5hMeTSJy8_ado';
        const newUserEmail: IUserData = {
            EmailAddress: companyAddress && companyAddress.address ? companyAddress.address.Email : ''
        };

        // Check existing user if setting.
        const successPageURL =
            (this.props.config.confirmationLink &&
                this.props.config.confirmationLink.linkUrl &&
                this.props.config.confirmationLink.linkUrl.destinationUrl) ||
            getUrlSync('home', this.props.context.actionContext);

        if (userAPIEndpoint) {
            event.preventDefault();
            UserDataService.checkExist(userAPIEndpoint, newUserEmail)
                .then((response: any) => {
                    if (response.data.Id && response.data.Id !== '') {
                        // Show Error message and stop process.
                        this.setState({
                            isAccountExist: true,
                            isReady: false
                        });
                    } else {
                        this.setState({
                            isAccountExist: false,
                            isReady: false
                        });
                        this._createProspectAsync(prospectAPIEndpoint, newPartner, successPageURL, context.actionContext);
                    }
                })
                .catch((e: Error) => {
                    this.setState({
                        hasSubmitErrors: true,
                        isReady: false
                    });
                });
        } else {
            this._createProspectAsync(prospectAPIEndpoint, newPartner, successPageURL, context.actionContext);
        }
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };

    private readonly _stringConvertTitleCase = (originText: string): string => {
        return originText
            .split(' ')
            .map(function(word) {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            .join(' ');
    };

    private readonly _createProspectAsync = (
        _prospectAPIEndpoint: string | undefined,
        _newPartner: BusinessPartnerProspect,
        _successPageURL: string | undefined,
        actionContext: IActionContext
    ) => {
        if (_prospectAPIEndpoint) {
            UserDataService.saveRegistrationLog(_prospectAPIEndpoint, { content: _newPartner }).then(() => {
                // Do nothing, never mind.
            });
        }
        createProspectAsync({ callerContext: actionContext }, _newPartner)
            .then(() => {
                if (_successPageURL && !this.state.hasSubmitErrors) {
                    window.location.href = _successPageURL;
                }
            })
            .catch(() => {
                this.setState({
                    hasSubmitErrors: true,
                    isReady: false
                });
            });
    };
}

export default BusinessSignUp;
